import type { InfoClient } from "@nktkas/hyperliquid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { queryKeys } from "#/hooks/query-keys";
import {
	useHyperAccount,
	useHyperOpenOrders,
} from "#/hooks/use-hyperliquid-account";
import { useWallet } from "#/hooks/use-wallet";
import { getInfoClient } from "../lib/hyperliquid";
import { useHyperliquidSubscription } from "./use-hyperliquid-subscription";
import { useTradeMarket } from "./use-trade-market";

type ActiveAssetDataResponse = Awaited<
	ReturnType<InfoClient["activeAssetData"]>
>;

export function useTradeAccount() {
	const { market } = useTradeMarket();
	const { accountState, positions: allPositions, ...query } = useHyperAccount();

	const positions = useMemo(
		() =>
			[...allPositions].sort((a, b) =>
				a.coin === market.coin ? -1 : b.coin === market.coin ? 1 : 0,
			),
		[allPositions, market.coin],
	);

	return {
		...query,
		positions,
		accountState,
	};
}

export function useTradeActiveAssetData() {
	const { address } = useWallet();
	const { market } = useTradeMarket();
	const infoClient = getInfoClient();
	const queryClient = useQueryClient();
	const apiCoin = market.chartSymbol;

	const query = useQuery({
		queryKey: queryKeys.trade.activeAssetData(address, apiCoin),
		queryFn: () =>
			infoClient.activeAssetData({
				user: address as `0x${string}`,
				coin: apiCoin,
			}) as Promise<ActiveAssetDataResponse>,
		staleTime: 300_000,
		enabled: address !== null,
	});

	useHyperliquidSubscription({
		enabled: address !== null && apiCoin.length > 0,
		deps: [address, apiCoin],
		subscribe: (client) =>
			client.activeAssetData(
				{ user: address as `0x${string}`, coin: apiCoin },
				(data) => {
					queryClient.setQueryData(
						queryKeys.trade.activeAssetData(address, apiCoin),
						data as ActiveAssetDataResponse,
					);
				},
			),
	});

	return {
		...query,
		activeAssetData: query.data as ActiveAssetDataResponse | undefined,
	};
}

export function useTradeOpenOrders() {
	const { market } = useTradeMarket();
	const { openOrders: allOrders, ...query } = useHyperOpenOrders();

	const openOrders = useMemo(
		() =>
			[...allOrders].sort((a, b) =>
				a.coin === market.coin ? -1 : b.coin === market.coin ? 1 : 0,
			),
		[allOrders, market.coin],
	);

	return {
		...query,
		openOrders,
	};
}
